/**
 * Email Broadcast API Service
 * API functions for the self-contained email broadcast system
 *
 * Created: January 3, 2026
 */

import { BASE_URL } from "../../data/BaseUrl";

// Helper function to build query string
const buildQueryString = (filters) => {
  const filteredParams = Object.entries(filters)
    .filter(([_, value]) => value !== "" && value !== null && value !== undefined)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return filteredParams ? `?${filteredParams}` : "";
};

// Helper function for API calls
const apiCall = async (url, method, token, body = null) => {
  const options = {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  const response = await fetch(`${BASE_URL}/${url}`, options);
  return response.json();
};

// ==================== LISTS ====================

export const fetchEmailLists = async (filters, token) => {
  const url = `email-broadcast/list${buildQueryString(filters)}`;
  return apiCall(url, "GET", token);
};

export const fetchEmailListById = async (id, token) => {
  const url = `email-broadcast/list/${id}`;
  return apiCall(url, "GET", token);
};

export const createEmailList = async (data, token) => {
  return apiCall("email-broadcast/list", "POST", token, data);
};

export const updateEmailList = async (id, data, token) => {
  return apiCall(`email-broadcast/list/${id}`, "PUT", token, data);
};

export const deleteEmailList = async (id, token) => {
  return apiCall(`email-broadcast/list/${id}`, "DELETE", token);
};

// ==================== SUBSCRIBERS ====================

export const fetchEmailSubscribers = async (filters, token) => {
  const url = `email-broadcast/subscriber${buildQueryString(filters)}`;
  return apiCall(url, "GET", token);
};

export const addEmailSubscriber = async (data, token) => {
  return apiCall("email-broadcast/subscriber", "POST", token, data);
};

export const bulkAddEmailSubscribers = async (data, token) => {
  return apiCall("email-broadcast/subscriber/bulk", "POST", token, data);
};

export const importEmailSubscribers = async (data, token) => {
  return apiCall("email-broadcast/subscriber/import", "POST", token, data);
};

export const exportEmailSubscribers = async (filters, token) => {
  const url = `email-broadcast/subscriber/export${buildQueryString(filters)}`;
  return apiCall(url, "GET", token);
};

export const fetchSubscriberActivity = async (subscriberId, token) => {
  const url = `email-broadcast/subscriber/${subscriberId}/activity`;
  return apiCall(url, "GET", token);
};

// ==================== TEMPLATES ====================

export const fetchEmailTemplates = async (filters, token) => {
  const url = `email-broadcast/template${buildQueryString(filters)}`;
  return apiCall(url, "GET", token);
};

export const fetchEmailTemplateById = async (id, token) => {
  const url = `email-broadcast/template/${id}`;
  return apiCall(url, "GET", token);
};

export const createEmailTemplate = async (data, token) => {
  return apiCall("email-broadcast/template", "POST", token, data);
};

export const updateEmailTemplate = async (id, data, token) => {
  return apiCall(`email-broadcast/template/${id}`, "PUT", token, data);
};

export const deleteEmailTemplate = async (id, token) => {
  return apiCall(`email-broadcast/template/${id}`, "DELETE", token);
};

export const duplicateEmailTemplate = async (id, token) => {
  return apiCall(`email-broadcast/template/${id}/duplicate`, "POST", token);
};

// ==================== CAMPAIGNS ====================

export const fetchEmailCampaigns = async (filters, token) => {
  const url = `email-broadcast/campaign${buildQueryString(filters)}`;
  return apiCall(url, "GET", token);
};

export const fetchEmailCampaignById = async (id, token) => {
  const url = `email-broadcast/campaign/${id}`;
  return apiCall(url, "GET", token);
};

export const fetchEmailCampaignStats = async (id, token) => {
  const url = `email-broadcast/campaign/${id}/stats`;
  return apiCall(url, "GET", token);
};

export const createEmailCampaign = async (data, token) => {
  return apiCall("email-broadcast/campaign", "POST", token, data);
};

export const updateEmailCampaign = async (id, data, token) => {
  return apiCall(`email-broadcast/campaign/${id}`, "PUT", token, data);
};

export const deleteEmailCampaign = async (id, token) => {
  return apiCall(`email-broadcast/campaign/${id}`, "DELETE", token);
};

export const scheduleCampaign = async (id, data, token) => {
  return apiCall(`email-broadcast/campaign/${id}/schedule`, "POST", token, data);
};

export const sendCampaignNow = async (id, token) => {
  return apiCall(`email-broadcast/campaign/${id}/send`, "POST", token);
};

export const pauseCampaign = async (id, token) => {
  return apiCall(`email-broadcast/campaign/${id}/pause`, "POST", token);
};

export const resumeCampaign = async (id, token) => {
  return apiCall(`email-broadcast/campaign/${id}/resume`, "POST", token);
};

export const duplicateCampaign = async (id, token) => {
  return apiCall(`email-broadcast/campaign/${id}/duplicate`, "POST", token);
};

// ==================== ANALYTICS & STATS ====================

export const fetchEmailAnalytics = async (filters, token) => {
  const url = `email-broadcast/analytics${buildQueryString(filters)}`;
  return apiCall(url, "GET", token);
};

export const fetchSESQuota = async (token) => {
  return apiCall("email-broadcast/ses/quota", "GET", token);
};

export const fetchQueueStats = async (filters, token) => {
  const url = `email-broadcast/queue/stats${buildQueryString(filters)}`;
  return apiCall(url, "GET", token);
};

export const retryFailedEmails = async (data, token) => {
  return apiCall("email-broadcast/queue/retry", "POST", token, data);
};

// ==================== PREVIEW & TEST ====================

export const previewEmail = async (data, token) => {
  return apiCall("email-broadcast/preview", "POST", token, data);
};

export const sendTestEmail = async (data, token) => {
  return apiCall("email-broadcast/test-send", "POST", token, data);
};

// ==================== SUPPRESSION LIST ====================

export const fetchSuppressionList = async (filters, token) => {
  const url = `email-broadcast/suppression${buildQueryString(filters)}`;
  return apiCall(url, "GET", token);
};

export const addToSuppressionList = async (data, token) => {
  return apiCall("email-broadcast/suppression", "POST", token, data);
};

export const removeFromSuppressionList = async (email, token) => {
  return apiCall(`email-broadcast/suppression/${encodeURIComponent(email)}`, "DELETE", token);
};

// ==================== USER TYPES ====================

export const fetchUserTypes = async (token) => {
  return apiCall("email-broadcast/user-types", "GET", token);
};

// ==================== AUTO-POPULATE SUBSCRIBERS ====================

/**
 * Auto-populate email list subscribers from WTF database
 * @param {string} listId - List ID or UID
 * @param {object} data - { user_type, filters, subscriber_ids }
 * @param {string} token - JWT token
 */
export const populateEmailListSubscribers = async (listId, data, token) => {
  return apiCall(`email-broadcast/list/${listId}/populate`, "POST", token, data);
};
